import React    from "react"
import Sortable from "sortablejs"
import Base     from "../base"

// Lines
import LineBpu     from "./line_bpu"
import Product     from "./line_product"
import Break       from "./line_break"
import Blueprint   from "./line_blueprint"
import Descriptive from "./line_descriptive"
import Regular     from "./line_regular"
import Service     from "./line_service"
import Section     from "./line_section"
import SubTotal    from "./line_subtotal"
import Group       from "./line_group"
import ImportCsv   from "./line_import_csv"
import Template    from "./line_template"
import Destroyed   from "./line_destroyed"
import AddButtons  from "./add_buttons"
import Totals      from "./totals"

// Helpers
import { setSubTotals, setSections, quoteCalculations } from "../helpers"
import { defaultCostCategoryFor }                       from "./shared/category_styles"
import { quoteVatRate }                                 from "@shared/quotes/resolveVatRate"

const STATUS_DEMAND = "Demand"

const buildQline = (type, quote) => {
  const baseLine = {
    active:          true,
    option:          false,
    hide_in_pdf:     false,
    can_be_deleted:  true,
    vat_rate:        quoteVatRate(quote) || {},
    discount:        quote.client.discount,
    currency_symbol: quote.currency_symbol,
    currency:        quote.currency,
    product:         {},
    cost_category:   defaultCostCategoryFor(type),
  }

  switch (type) {
    case "product":
      return({...baseLine, type: "product"})
    case "break":
      return({...baseLine, type: "break", active: false})
    case "blueprint":
      return({...baseLine, type: "blueprint"})
    case "subtotal":
      return({...baseLine, type: "subtotal", description: I18n.t("quotes.bigcard.subtotal"), quantity: 1})
    case "section":
      return({...baseLine, type: "section"})
    case "group":
      return({...baseLine, type: "group"})
    case "template":
      return({...baseLine, type: "template"})
    case "descriptive":
      return({...baseLine, type: "descriptive"})
    case "regular":
      return({...baseLine, type: "regular"})
    case "service":
      return({
        ...baseLine,
        type: "service",
        quantity: 1,
        _service_init_mode: "choose",
        qline_breakdowns: []
      })
    case "import_csv":
      return({...baseLine, type: "import_csv"})
    case "bpu":
      return({...baseLine, type: "bpu", quantity: 1, qline_breakdowns: []})
  }
}

const Qline = ({quote, order, qline, lineProps}) =>
  {
    switch(qline.type) {
      case "product":
        return(
          <Product
            key         = {qline.id}
            quote       = {quote}
            qline       = {qline}
            index       = {order}
            vats        = {lineProps.vats}
            showCode    = {lineProps.showCode}
            showMargin  = {lineProps.showMargin}
            buttonProps = {lineProps.buttonProps}
          />
        )
      case "bpu":
        return(
          <LineBpu
            key         = {qline.id}
            quote       = {quote}
            qline       = {qline}
            index       = {order}
            vats        = {lineProps.vats}
            showCode    = {lineProps.showCode}
            showMargin  = {lineProps.showMargin}
            buttonProps = {lineProps.buttonProps}
          />
        )
      case "break":
        return(
          <Break
            key         = {qline.id}
            qline       = {qline}
            index       = {order}
            buttonProps = {lineProps.buttonProps}
          />
        )
      case "blueprint":
        return(
          <Blueprint
            key         = {qline.id}
            qline       = {qline}
            index       = {order}
            blueprints  = {lineProps.blueprints}
            buttonProps = {lineProps.buttonProps}
          />
        )
      case "subtotal":
        return(
          <SubTotal
            key         = {qline.id}
            qline       = {qline}
            index       = {order}
            showCode    = {lineProps.showCode}
            buttonProps = {lineProps.buttonProps}
          />
        )
      case "section": 
        return(
          <Section
            key         = {qline.id}
            quote       = {quote}
            qline       = {qline}
            index       = {order}
            showCode    = {lineProps.showCode}
            buttonProps = {lineProps.buttonProps}
          />
        )
      case "group":
        return(
          <Group
            key         = {qline.id}
            qline       = {qline}
            index       = {order}
            groupProps  = {lineProps.groupProps}
            buttonProps = {lineProps.buttonProps}
          />
        )
      case "template":
        return(
          <Template
            key           = {qline.id}
            qline         = {qline}
            index         = {order}
            templateProps = {lineProps.templateProps}
            buttonProps   = {lineProps.buttonProps}
          />
        )
      case "descriptive":
        return(
          <Descriptive
            key         = {qline.id}
            qline       = {qline}
            index       = {order}
            showCode    = {lineProps.showCode}
            buttonProps = {lineProps.buttonProps}
          />
        )
      case "regular":
        return(
          <Regular
            key         = {qline.id}
            quote       = {quote}
            qline       = {qline}
            index       = {order}
            showCode    = {lineProps.showCode}
            showMargin  = {lineProps.showMargin}
            vats        = {lineProps.vats}
            buttonProps = {lineProps.buttonProps}
          />
        )
      case "service":
        return(
          <Service
            key                      = {qline.id}
            quote                    = {quote}
            qline                    = {qline}
            index                    = {order}
            showCode                 = {lineProps.showCode}
            showMargin               = {lineProps.showMargin}
            vats                     = {lineProps.vats}
            qline_service_blueprints = {lineProps.qline_service_blueprints}
            buttonProps              = {lineProps.buttonProps}
          />
        )
      case "import_from_csv":
        return(
          <ImportCsv
            key         = {qline.id}
            quote       = {quote}
            qline       = {qline}
            index       = {order}
            addFromCsv  = {lineProps.addFromCsv}
            buttonProps = {lineProps.buttonProps}
          />
        )
    }
  }

const QlinesContainer = React.forwardRef(({quote, qlines, lineProps}, containerRef) =>
  <div className="qlines-container" ref={containerRef}>
    {qlines.map((qline, index) =>
      <div
        key       = {`${index}-${qline.active}`}
        className = "qline-sortable-item"
      >
        <Qline
          order     = {index}
          quote     = {quote}
          qline     = {qline}
          lineProps = {lineProps}
        />
      </div>
    )}
  </div>
)

export default class Qlines extends Base {
  constructor(props) {
    super(props)
    this.replaceLine  = this.replaceLine.bind(this)
    this.moveLine     = this.moveLine.bind(this)
    this.addLine      = this.addLine.bind(this)
    this.addGroup     = this.addGroup.bind(this)
    this.addTemplate  = this.addTemplate.bind(this)
    this.addFromCsv   = this.addFromCsv.bind(this)
    this.containerRef = React.createRef()
  }

  componentDidMount() {
    if (!this.containerRef.current) return

    this.sortable = new Sortable(this.containerRef.current, {
      animation: 150,
      handle:    ".qline-drag-handle",
      draggable: ".qline-sortable-item",
      onStart: (event) => {
        event.from._savedOrder = [...event.from.children]
      },
      onEnd: (event) => {
        const saved = event.from._savedOrder
        if (saved) {
          saved.forEach(child => event.from.appendChild(child))
          delete event.from._savedOrder
        }
        const oldIndex = event.oldDraggableIndex != null ? event.oldDraggableIndex : event.oldIndex
        const newIndex = event.newDraggableIndex != null ? event.newDraggableIndex : event.newIndex
        if (oldIndex !== newIndex) this.moveLine(oldIndex, newIndex)
      }
    })
  }

  componentWillUnmount() {
    if (this.sortable) {
      this.sortable.destroy()
      this.sortable = null
    }
  }

  moveLine(index, newIndex) {
    const { quote, replaceQuote } = this.props
    const qlines = quote.qlines
    if (newIndex >= qlines.length) { newIndex = qlines.length }
    qlines.splice(newIndex, 0, qlines.splice(index, 1)[0])
    setSubTotals(quote)
    setSections(quote)
    replaceQuote(quote)
  }

  replaceLine(qline, index) {
    const { quote, replaceQuote } = this.props
    quote.qlines[index] = qline
    if (qline.destroyed) {
      const qlines         = quote.qlines.filter(qline => !qline.destroyed)
      const destroyedLines = quote.qlines.filter(qline => qline.destroyed)
      quote.qlines = [...qlines, ...destroyedLines]
    }
    setSubTotals(quote)
    setSections(quote)
    replaceQuote(quote)
  }

  addLine(type, index) {
    const { quote, replaceQuote } = this.props
    let   buffer   = quote.qlines
    const previous = buffer.splice(0, index)
    const qline    = buildQline(type, quote)
    quote.qlines = [...previous, qline, ...buffer]
    setSubTotals(quote)
    setSections(quote)
    replaceQuote(quote)
  }

  addGroup(group, amount, unit, index) {
    const { quote, replaceQuote } = this.props
    // Set lines as inactive & it's vat rate
    group.forEach(line => {
      if (line.type == "subtotal") {
        line.quantity = amount
        line.unit     = unit
      } else if (line.quantity) {
        line.quantity = line.quantity * amount
      }

      line.active   = false
      line.vat_rate = quoteVatRate(quote) || {}
      line.discount = quote.client.discount
    })
    let buffer = quote.qlines
    // Remove the actual group line
    buffer.splice(index, 1)
    // Divide qlines in 2 arrays, between the index
    const previous = buffer.splice(0, index)
    quote.qlines = [...previous, ...group, ...buffer]
    setSubTotals(quote)
    setSections(quote)
    replaceQuote(quote)
  }

  addFromCsv(new_lines, index) {
    const { quote, replaceQuote } = this.props
    new_lines.forEach(line => line.active = false)
    let buffer = quote.qlines
    // Remove the actual group line
    buffer.splice(index, 1)
    // Divide qlines in 2 arrays, between the index
    const previous = buffer.splice(0, index)
    quote.qlines = [...previous, ...new_lines, ...buffer]
    setSubTotals(quote)
    setSections(quote)
    replaceQuote(quote)
  }

  addTemplate(template, index) {
    const { quote, replaceQuote } = this.props
    template.qlines.forEach(line => {
      line.active   = false
      line.vat_rate = quoteVatRate(quote) || {}
    })

    const previousLines = quote.qlines.filter((_line, position) => position != index)
    const keptLines     = previousLines.filter(line => !line.destroyed)
    const removedLines  = previousLines.filter(line => line.destroyed)

    const placeholder = quote.qlines[index]
    if (placeholder && placeholder.id) removedLines.push({...placeholder, active: false, destroyed: true})

    replaceQuote({
      ...quote,
      title:        quote.title        || template.title,
      qlines:       [...template.qlines, ...keptLines, ...removedLines],
      observations: quote.observations || template.observations
    })
  }

  render() {
    const { quote, replaceQuote } = this.props

    const qlines         = quote.qlines.filter(qline => !qline.destroyed)
    const destroyedLines = quote.qlines.filter(qline => qline.destroyed)
    const calculations   = quoteCalculations(quote)

    const lineProps = {
      showCode:                 quote.account.show_code,
      showMargin:               quote.show_margin,
      vats:                     quote.account.vat_rates,
      blueprints:               quote.account.blueprints,
      qline_service_blueprints: quote.account.qline_service_blueprints || [],
      allowQlineServices:       quote.account.allow_qline_services,
      groupProps: {
        groups:   quote.account.line_groups,
        addGroup: this.addGroup,
      },
      templateProps: {
        templates:   quote.account.templates,
        addTemplate: this.addTemplate,
        showConfirm: qlines.length > 1
      },
      buttonProps: {
        addLine:              this.addLine,
        replaceLine:          this.replaceLine,
        moveLine:             this.moveLine,
        allowCsvLine:         quote.account.csv_qlines,
        allowProducts:        quote.account.allow_products,
        allowQlineServices:   quote.account.allow_qline_services,
        allowQlineGroups:     quote.account.allow_qline_groups,
        allowQlineBlueprints: quote.account.allow_qline_blueprints,
        hasMbcContract:       !!quote.mbc_contract?.id,
        allowTemplate:        quote.new_record || quote.status == STATUS_DEMAND,
        showCostCategory:       !!quote.account.show_estimated_cost_breakdown,
        // Overridden per category on the quote (cf. CostBreakdownTable
        // "Apply" summary). The quote overrides take precedence over the
        // account default ↦ each line immediately shows the right
        // cost price (= PA × (1 + applied overhead coef)) after a click.
        overheadCoefs:          {
                                  ...(quote.account.overhead_coefs || {}),
                                  ...(quote.overhead_coef_overrides || {}),
                                },
        // The quote override first, the account default next. A missing
        // category means nothing was set, and leaves the selling price alone.
        saleCoefOverrides:      {
                                  ...(quote.account.sale_coefs || {}),
                                  ...(quote.sale_coef_overrides || {}),
                                },
        // Fallback coef when a category-specific value is not provided.
        overheadCoef:           (parseFloat(quote.account.overhead_ratio) || 0) / 100,
        // List of categories the account has chosen to expose. Empty / missing
        // means "all 5" (legacy accounts pre-feature).
        enabledCostCategories:  quote.account.enabled_cost_categories || []
      },
      addFromCsv:     this.addFromCsv
    }

    return(
      <React.Fragment>
        <div className="quotes--form-table">

          { quote.show_table_header &&
            <div className="quotes--form-table-header">
              <div className="quotes--form-table-row">
                { quote.account.show_code
                  ? <div className="code-column">
                      {I18n.t("quotes.pdf.service")}
                    </div>
                  : ""
                }
                <div className="description-column">
                  {I18n.t("quotes.pdf.description")}
                </div>
                <div className="price-column">
                  {I18n.t("quotes.pdf.price")}
                </div>
                <div className="amount-column">
                  {I18n.t("quotes.pdf.quantity")}
                </div>
                <div className="unit-column">
                  {I18n.t("quotes.pdf.unit")}
                </div>
                <div className="vat-column">
                  {I18n.t("quotes.pdf.vat")}
                </div>
                <div className="total-column">
                  {I18n.t("quotes.pdf.hvta")}
                </div>
              </div>
            </div>
          }

          <QlinesContainer
            ref       = {this.containerRef}
            quote     = {quote}
            qlines    = {qlines}
            lineProps = {lineProps}
          />

          {
            destroyedLines.map((qline, index) =>
              <Destroyed
                key   = {index}
                index = {qlines.length + index}
                qline = {qline}
              />
            )
          }
        </div>

        <div className="quotes--form-table-footer">
          <AddButtons
            quote   = {quote}
            onClick = {this.addLine}
            index   = {qlines.length}
          />

          <Totals
            quote        = {quote}
            calculations = {calculations}
            replaceQuote = {replaceQuote}
          />
        </div>
      </React.Fragment>
    )
  }
}
